import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, type SyncJobRun } from '@/api';

export function SyncJobsList() {
  const [runs, setRuns] = useState<SyncJobRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);

  const load = () => {
    setLoading(true);
    adminApi.syncJobs
      .listRuns()
      .then(setRuns)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleTrigger = async () => {
    setTriggering(true);
    setError(null);
    try {
      await adminApi.syncJobs.trigger();
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Trigger failed');
    } finally {
      setTriggering(false);
    }
  };

  if (loading && runs.length === 0) return <div className="text-slate-500">Loading…</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h1 className="text-2xl font-semibold text-slate-800">Sync Jobs</h1>
        <div className="flex gap-2">
          <Link
            to="/syncs"
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Sync Articles
          </Link>
          <button
            type="button"
            onClick={handleTrigger}
            disabled={triggering}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {triggering ? 'Running…' : 'Run sync job'}
          </button>
        </div>
      </div>
      <p className="text-slate-600 mb-4">
        Auto-generate AI sync articles from RSS feeds. Articles are created as drafts.
      </p>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Started</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Processed</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Skipped</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {runs.map((r) => (
              <tr key={r.id}>
                <td className="px-4 py-3 text-slate-800">
                  {new Date(r.startedAt).toLocaleString()}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                      r.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : r.status === 'failed'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-600">{r.processed}</td>
                <td className="px-4 py-3 text-slate-600">{r.skipped}</td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`/syncs/jobs/${r.id}`}
                    className="text-indigo-600 hover:underline text-sm font-medium"
                  >
                    Details
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {runs.length === 0 && (
        <p className="text-slate-500 mt-4">No sync job runs yet. Click "Run sync job" to start.</p>
      )}
    </div>
  );
}
